import { FC, useCallback } from 'react';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { MedicalImageInterface } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';

import { MedicalImagesGroups } from '@/entities/assets';
import { organizationModel } from '@/entities/organization';

import {
  MedicalImage,
  useAddAndRemoveMedicalImage,
} from '@/features/medicalImage';
import { maskFiltersModel } from '@/features/renderMasks';

import { DragAndDropHint } from '../DragAndDropHint/DragAndDropHint';
import { MedicalImagesDnD } from '../MedicalImagesDnD/MedicalImagesDnD';

type SelectedMedicalImageListProps = {
  toothID: string;
  canUserDeleteSlices?: boolean;
  medicalImages?: MedicalImageInterface[];
  showDragAndDrop?: boolean;
  onDropSlice?: (sliceID: string) => void;
  onMedicalImageClick?: (medicalImage: MedicalImageInterface) => void;
  onRemoveMedicalImage?: (medicalImage: MedicalImageInterface) => void;
};

export const SelectedMedicalImageList: FC<SelectedMedicalImageListProps> = (
  props,
) => {
  const {
    toothID,
    canUserDeleteSlices = true,
    medicalImages,
    showDragAndDrop = true,
    onDropSlice,
    onMedicalImageClick,
    onRemoveMedicalImage,
  } = props;

  const { isMobile } = useMedia();

  const { removeMedicalImageFromSelected } = useAddAndRemoveMedicalImage();

  const hidePerioMasksAndSlices = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_PerioMasksAndSlices,
    ),
  );

  const { isConditionsMasksHidden, isCBCTMasksHidden } = useAppSelector(
    maskFiltersModel.selectors.selectMasksFlags,
  );

  const handleDeleteMedicalImage = (medicalImage: MedicalImageInterface) => {
    if (onRemoveMedicalImage) {
      onRemoveMedicalImage(medicalImage);
      return;
    }

    removeMedicalImageFromSelected(medicalImage.assetID, toothID);
  };

  const checkIsTheMedicalImageSelected = useCallback(
    (assetID: string) => {
      return (
        medicalImages?.some(
          (medicalImage) => medicalImage.assetID === assetID,
        ) ?? false
      );
    },
    [medicalImages],
  );

  if (!medicalImages) {
    return null;
  }

  return (
    <>
      {medicalImages
        .filter((medicalImage) => {
          if (medicalImage.groupName === MedicalImagesGroups.PeriapicalLesion) {
            return (
              !hidePerioMasksAndSlices &&
              !isConditionsMasksHidden &&
              !isCBCTMasksHidden
            );
          }

          return true;
        })
        .map((medicalImage) => (
          <MedicalImage
            key={medicalImage.src}
            medicalImage={medicalImage}
            order={medicalImage.order}
            onRemoveMedicalImage={
              canUserDeleteSlices ? handleDeleteMedicalImage : undefined
            }
            isImgSelected
            canUserDeleteSlices={canUserDeleteSlices}
            onImageClick={
              onMedicalImageClick
                ? () => onMedicalImageClick(medicalImage)
                : undefined
            }
          />
        ))}

      {!isMobile && showDragAndDrop && (
        <>
          <MedicalImagesDnD
            toothID={toothID}
            checkIsTheMedicalImageSelected={checkIsTheMedicalImageSelected}
            onDropSlice={onDropSlice}
          />

          <DragAndDropHint />
        </>
      )}
    </>
  );
};
